import React, { useState, useEffect } from 'react';
import { 
  Award,
  Star,
  TrendingUp,
  Target,
  Gift,
  Crown,
  Zap,
  Shield,
  Trophy,
  CheckCircle,
  Clock,
  Users,
  DollarSign,
  BarChart3,
  Calendar,
  ArrowRight,
  Info,
  Sparkles,
  Medal,
  Gem
} from 'lucide-react';
import { ToastContext } from '../ToastProvider';

export default function TierSystem({ affiliateId, userId }) {
  const { notify } = React.useContext(ToastContext);
  
  const [currentTier, setCurrentTier] = useState('bronze');
  const [tierProgress, setTierProgress] = useState({});
  const [tierHistory, setTierHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTierDetails, setShowTierDetails] = useState(false);

  // Tier definitions with requirements and benefits
  const tierDefinitions = {
    bronze: {
      name: 'Bronze',
      icon: Medal,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-800',
      requirements: {
        minSales: 0,
        minReferrals: 0,
        minEarnings: 0,
        minMonths: 0
      },
      benefits: [
        '3% base commission rate',
        'Standard support',
        'Basic analytics',
        'Email notifications'
      ],
      nextTier: 'silver'
    },
    silver: {
      name: 'Silver',
      icon: Star,
      color: 'from-gray-400 to-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      textColor: 'text-gray-800',
      requirements: {
        minSales: 10000,
        minReferrals: 10,
        minEarnings: 500,
        minMonths: 1
      },
      benefits: [
        '4% base commission rate',
        'Priority support',
        'Advanced analytics',
        'SMS notifications',
        'Custom referral links'
      ],
      nextTier: 'gold'
    },
    gold: {
      name: 'Gold',
      icon: Crown,
      color: 'from-yellow-400 to-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      requirements: {
        minSales: 50000,
        minReferrals: 25,
        minEarnings: 2500,
        minMonths: 3
      },
      benefits: [
        '5% base commission rate',
        'VIP support',
        'Premium analytics',
        'Push notifications',
        'Exclusive products access',
        'Live selling tools',
        'Marketing materials'
      ],
      nextTier: 'platinum'
    },
    platinum: {
      name: 'Platinum',
      icon: Gem,
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-800',
      requirements: {
        minSales: 100000,
        minReferrals: 50,
        minEarnings: 5000,
        minMonths: 6
      },
      benefits: [
        '6% base commission rate',
        'Dedicated account manager',
        'Real-time analytics',
        'All notification types',
        'Exclusive product launches',
        'Advanced live selling',
        'Custom marketing campaigns',
        'Revenue sharing bonus'
      ],
      nextTier: null
    }
  };

  // Mock current affiliate data
  const mockAffiliateData = {
    currentTier: 'bronze',
    tierProgress: {
      sales: 2500,
      referrals: 3,
      earnings: 125.50,
      monthsActive: 0.5
    },
    tierHistory: [
      {
        tier: 'bronze',
        achievedAt: '2024-01-01',
        sales: 0,
        referrals: 0,
        earnings: 0
      }
    ],
    nextTierProgress: {
      sales: 7500, // 10000 - 2500
      referrals: 7, // 10 - 3
      earnings: 374.50, // 500 - 125.50
      monthsActive: 0.5 // 1 - 0.5
    }
  };

  useEffect(() => {
    loadTierData();
  }, [affiliateId]);

  const loadTierData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setCurrentTier(mockAffiliateData.currentTier);
      setTierProgress(mockAffiliateData.tierProgress);
      setTierHistory(mockAffiliateData.tierHistory);
    } catch (error) {
      console.error('Error loading tier data:', error);
      notify('Failed to load tier information', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const getProgressPercentage = (current, required) => {
    return Math.min((current / required) * 100, 100);
  };

  const getTierIcon = (tier) => {
    const IconComponent = tierDefinitions[tier].icon;
    return <IconComponent className="w-6 h-6" />;
  };

  const getTierColor = (tier) => {
    return tierDefinitions[tier].color;
  };

  const getNextTier = () => {
    const currentTierDef = tierDefinitions[currentTier];
    return currentTierDef.nextTier ? tierDefinitions[currentTierDef.nextTier] : null;
  };

  const getNextTierProgress = () => {
    const nextTier = getNextTier();
    if (!nextTier) return null;

    const current = tierProgress;
    const required = nextTier.requirements;

    return {
      sales: Math.max(0, required.minSales - current.sales),
      referrals: Math.max(0, required.minReferrals - current.referrals),
      earnings: Math.max(0, required.minEarnings - current.earnings),
      monthsActive: Math.max(0, required.minMonths - current.monthsActive)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentTierDef = tierDefinitions[currentTier];
  const nextTier = getNextTier();
  const nextTierProgress = getNextTierProgress();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Affiliate Tier System</h1>
          <p className="text-gray-600">Track your progress and unlock higher commission rates</p>
        </div>
        <button
          onClick={() => setShowTierDetails(!showTierDetails)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Info className="w-4 h-4" />
          <span>View All Tiers</span>
        </button>
      </div>

      {/* Current Tier Card */}
      <div className={`${currentTierDef.bgColor} ${currentTierDef.borderColor} border-2 rounded-xl p-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-full bg-gradient-to-r ${currentTierDef.color} text-white`}>
              {getTierIcon(currentTier)}
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${currentTierDef.textColor}`}>
                {currentTierDef.name} Tier
              </h2>
              <p className="text-gray-600">Your current affiliate level</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Commission Rate</div>
            <div className={`text-3xl font-bold ${currentTierDef.textColor}`}>
              {currentTier === 'bronze' ? '3%' : 
               currentTier === 'silver' ? '4%' : 
               currentTier === 'gold' ? '5%' : '6%'}
            </div>
          </div>
        </div>

        {/* Current Tier Benefits */}
        <div className="mt-6">
          <h3 className={`text-lg font-semibold ${currentTierDef.textColor} mb-3`}>
            Your Benefits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currentTierDef.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2">
                <CheckCircle className={`w-4 h-4 ${currentTierDef.textColor}`} />
                <span className="text-sm text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress to Next Tier */}
      {nextTier && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full bg-gradient-to-r ${nextTier.color} text-white`}>
                {getTierIcon(nextTier.name.toLowerCase())}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Progress to {nextTier.name} Tier
                </h3>
                <p className="text-sm text-gray-600">Complete these requirements to advance</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Overall Progress</div>
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(
                  (tierProgress.sales / nextTier.requirements.minSales * 25) +
                  (tierProgress.referrals / nextTier.requirements.minReferrals * 25) +
                  (tierProgress.earnings / nextTier.requirements.minEarnings * 25) +
                  (tierProgress.monthsActive / nextTier.requirements.minMonths * 25)
                )}%
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Sales Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Total Sales</span>
                <span className="text-sm text-gray-600">
                  {formatCurrency(tierProgress.sales)} / {formatCurrency(nextTier.requirements.minSales)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage(tierProgress.sales, nextTier.requirements.minSales)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500">
                {formatCurrency(nextTierProgress.sales)} more needed
              </div>
            </div>

            {/* Referrals Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Referrals</span>
                <span className="text-sm text-gray-600">
                  {tierProgress.referrals} / {nextTier.requirements.minReferrals}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage(tierProgress.referrals, nextTier.requirements.minReferrals)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500">
                {nextTierProgress.referrals} more needed
              </div>
            </div>

            {/* Earnings Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Earnings</span>
                <span className="text-sm text-gray-600">
                  {formatCurrency(tierProgress.earnings)} / {formatCurrency(nextTier.requirements.minEarnings)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage(tierProgress.earnings, nextTier.requirements.minEarnings)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500">
                {formatCurrency(nextTierProgress.earnings)} more needed
              </div>
            </div>

            {/* Months Active Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Months Active</span>
                <span className="text-sm text-gray-600">
                  {tierProgress.monthsActive} / {nextTier.requirements.minMonths}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage(tierProgress.monthsActive, nextTier.requirements.minMonths)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500">
                {nextTierProgress.monthsActive} more needed
              </div>
            </div>
          </div>

          {/* Next Tier Benefits Preview */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">
              {nextTier.name} Tier Benefits Preview
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {nextTier.benefits.slice(0, 4).map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Sparkles className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tier History */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tier History</h3>
        <div className="space-y-3">
          {tierHistory.map((entry, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full bg-gradient-to-r ${getTierColor(entry.tier)} text-white`}>
                  {getTierIcon(entry.tier)}
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {tierDefinitions[entry.tier].name} Tier Achieved
                  </div>
                  <div className="text-sm text-gray-600">
                    {new Date(entry.achievedAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="text-right text-sm text-gray-600">
                <div>{formatCurrency(entry.sales)} sales</div>
                <div>{entry.referrals} referrals</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* All Tiers Overview */}
      {showTierDetails && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">All Tier Levels</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(tierDefinitions).map(([tierKey, tierDef]) => (
              <div
                key={tierKey}
                className={`${tierDef.bgColor} ${tierDef.borderColor} border rounded-lg p-4 ${
                  tierKey === currentTier ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <div className={`p-2 rounded-full bg-gradient-to-r ${tierDef.color} text-white`}>
                    {getTierIcon(tierKey)}
                  </div>
                  <div>
                    <h4 className={`font-semibold ${tierDef.textColor}`}>{tierDef.name}</h4>
                    <div className="text-sm text-gray-600">
                      {tierKey === 'bronze' ? '3%' : 
                       tierKey === 'silver' ? '4%' : 
                       tierKey === 'gold' ? '5%' : '6%'} commission
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-700">Requirements:</div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div>Sales: {formatCurrency(tierDef.requirements.minSales)}</div>
                    <div>Referrals: {tierDef.requirements.minReferrals}</div>
                    <div>Earnings: {formatCurrency(tierDef.requirements.minEarnings)}</div>
                    <div>Months: {tierDef.requirements.minMonths}</div>
                  </div>
                </div>

                {tierKey === currentTier && (
                  <div className="mt-3 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium text-center">
                    Current Tier
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
